import React, { useState } from "react";
import { useApp } from "../state/AppContext";
import { logSiteClick } from "../lib/siteClick";
import { usePublicConfig } from "../lib/publicConfig";
import IntakeForm from "../components/IntakeForm";
import { HeartHandshake, Dices, ArrowRight, Sparkles, QrCode, BookOpen, CalendarClock, LogOut } from "lucide-react";

export default function HomeScreen() {
  const ctx = useApp();
  const { user, setScreen, t, resetUser, lang } = ctx;
  const publicCfg = usePublicConfig();
  const [showIntake, setShowIntake] = useState(false);
  const name = user?.first_name?.split(" ")[0] || "friend";

  const doLogout = () => {
    if (!user) return;
    if (window.confirm(t("logout_confirm"))) resetUser();
  };

  return (
    <div className="min-h-screen bg-scl-cream pt-6 pb-32 px-6" data-testid="home-screen">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-scl-teal" />
          <span className="text-xs uppercase tracking-[0.18em] text-scl-teal font-semibold">{t("home_kicker")}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setScreen("qr")} data-testid="open-qr" className="min-h-[44px] flex items-center gap-1.5 text-xs text-scl-charcoal/60 hover:text-scl-teal font-medium">
            <QrCode className="w-4 h-4" /> {t("view_qr")}
          </button>
          {user && (
            <button onClick={doLogout} data-testid="home-logout-btn" title={t("logout")} aria-label={t("logout")} className="min-h-[44px] min-w-[44px] flex items-center gap-1.5 text-xs text-scl-charcoal/60 hover:text-scl-brick font-medium px-2 border-l border-scl-border ml-1">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">{t("logout")}</span>
            </button>
          )}
        </div>
      </div>
      <h1 className="font-serif text-4xl leading-[1.05] text-scl-charcoal font-bold mb-2">
        {t("home_welcome_name", { name })}
      </h1>
      <p className="text-scl-charcoal/70 mb-8 leading-relaxed max-w-md">{t("home_lead")}</p>

      <button
        onClick={() => setScreen("gallery")}
        data-testid="path-kindness"
        className="relative w-full text-left rounded-3xl overflow-hidden mb-4 shadow-warm bg-white active:scale-[0.99] transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-scl-coral min-h-[44px]"
      >
        <div className="relative h-40">
          <img
            src="https://customer-assets-0z36b82j.emergentagent.net/job_scl-kindness/artifacts/lwsjtqkf_FullSizeRender%20%287%29.jpg"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-scl-coral/85 via-scl-coral/60 to-transparent" />
          <div className="absolute top-4 left-4 w-12 h-12 rounded-2xl bg-white/95 backdrop-blur flex items-center justify-center shadow-warm-sm">
            <HeartHandshake className="w-6 h-6 text-scl-coral" />
          </div>
          <div className="absolute bottom-3 right-3 bg-white/95 text-scl-charcoal text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full">
            {t("path_kindness_pill")}
          </div>
        </div>
        <div className="p-5 flex items-center gap-4">
          <div className="flex-1">
            <div className="font-serif text-2xl text-scl-charcoal font-bold leading-tight">{t("path_kindness_title")}</div>
            <div className="text-scl-charcoal/70 text-[15px] mt-1">{t("path_kindness_sub")}</div>
          </div>
          <div className="w-10 h-10 rounded-full bg-scl-coral text-white flex items-center justify-center flex-shrink-0"><ArrowRight className="w-5 h-5" /></div>
        </div>
      </button>

      <button
        onClick={() => setScreen("activities")}
        data-testid="path-activities"
        className="relative w-full text-left rounded-3xl overflow-hidden shadow-warm bg-white active:scale-[0.99] transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-scl-teal min-h-[44px]"
      >
        <div className="relative h-40">
          <img
            src="https://images.unsplash.com/photo-1469406396016-013bfae5d83e?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-scl-teal/85 via-scl-teal/60 to-transparent" />
          <div className="absolute top-4 left-4 w-12 h-12 rounded-2xl bg-white/95 backdrop-blur flex items-center justify-center shadow-warm-sm">
            <Dices className="w-6 h-6 text-scl-teal" />
          </div>
          <div className="absolute bottom-3 right-3 bg-white/95 text-scl-charcoal text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full">
            {t("path_activities_pill")}
          </div>
        </div>
        <div className="p-5 flex items-center gap-4">
          <div className="flex-1">
            <div className="font-serif text-2xl text-scl-charcoal font-bold leading-tight">{t("path_activities_title")}</div>
            <div className="text-scl-charcoal/70 text-[15px] mt-1">{t("path_activities_sub")}</div>
          </div>
          <div className="w-10 h-10 rounded-full bg-scl-teal text-white flex items-center justify-center flex-shrink-0"><ArrowRight className="w-5 h-5" /></div>
        </div>
      </button>

      {/* Secondary CTAs — Parents Guide + Book a Tour */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
        <button
          onClick={() => setShowIntake(true)}
          data-testid="home-parents-guide-cta"
          className="bg-white rounded-2xl p-4 shadow-warm-sm text-left hover:shadow-warm transition-shadow active:scale-[0.99] min-h-[44px]"
        >
          <div className="flex items-center gap-3 mb-1.5">
            <div className="w-9 h-9 rounded-xl bg-scl-coral/10 flex items-center justify-center flex-shrink-0"><BookOpen className="w-4 h-4 text-scl-coral" /></div>
            <div className="font-serif text-base font-bold text-scl-charcoal leading-tight">{t("home_parents_cta")}</div>
          </div>
          <p className="text-xs text-scl-charcoal/60 leading-relaxed">{t("home_parents_lead")}</p>
        </button>
        {publicCfg.tour_booking_url ? (
          <a
            href={lang === "es" ? `${publicCfg.tour_booking_url}${publicCfg.tour_booking_url.includes("?") ? "&" : "?"}hl=es` : publicCfg.tour_booking_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => logSiteClick(ctx, "home_book_tour", publicCfg.tour_booking_url)}
            data-testid="home-book-tour-cta"
            className="bg-white rounded-2xl p-4 shadow-warm-sm text-left hover:shadow-warm transition-shadow active:scale-[0.99] block min-h-[44px]"
          >
            <div className="flex items-center gap-3 mb-1.5">
              <div className="w-9 h-9 rounded-xl bg-scl-teal/10 flex items-center justify-center flex-shrink-0"><CalendarClock className="w-4 h-4 text-scl-teal" /></div>
              <div className="font-serif text-base font-bold text-scl-charcoal leading-tight">{t("home_tour_cta")}</div>
            </div>
            <p className="text-xs text-scl-charcoal/60 leading-relaxed">{t("home_tour_lead")}</p>
          </a>
        ) : (
          <div className="bg-scl-cream/60 rounded-2xl p-4 shadow-warm-sm border border-dashed border-scl-border" data-testid="home-book-tour-placeholder">
            <div className="flex items-center gap-3 mb-1.5">
              <div className="w-9 h-9 rounded-xl bg-scl-teal/10 flex items-center justify-center flex-shrink-0"><CalendarClock className="w-4 h-4 text-scl-teal/60" /></div>
              <div className="font-serif text-base font-bold text-scl-charcoal/60 leading-tight">{t("home_tour_cta")}</div>
            </div>
            <p className="text-xs text-scl-charcoal/50 leading-relaxed">Coming soon</p>
          </div>
        )}
      </div>

      {showIntake && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-6" onClick={() => setShowIntake(false)} data-testid="home-intake-modal">
          <div className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-warm max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <IntakeForm
              guide="parents"
              publicConfig={publicCfg}
              onClose={() => setShowIntake(false)}
            />
          </div>
        </div>
      )}

      <footer className="mt-8 text-center">
        <a
          href="https://schoolforcommunitylearning.org"
          target="_blank"
          rel="noreferrer"
          onClick={() => logSiteClick(ctx, "home_footer", "https://schoolforcommunitylearning.org")}
          data-testid="home-footer-link"
          className="text-xs text-scl-charcoal/55 hover:text-scl-teal underline underline-offset-4"
        >
          {t("footer_explore")}
        </a>
      </footer>
    </div>
  );
}
